import './firebaseEmulators'; // must stay first: points Firebase at local emulators when enabled
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { installAvatarFallback } from './utils/avatarCache';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('PadelMVP Uncaught Error Boundary:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '24px',
          backgroundColor: '#0f172a',
          color: '#f87171',
          minHeight: '100vh',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          zIndex: 999999
        }}>
          <h2 style={{ color: '#f59e0b', fontSize: '20px', marginBottom: '12px' }}>
            ⚠️ Padel MVP Application Error Caught
          </h2>
          <div style={{ color: '#ffffff', marginBottom: '16px', fontSize: '14px' }}>
            {this.state.error?.toString()}
          </div>
          <div style={{ color: '#94a3b8', fontSize: '12px', overflowX: 'auto' }}>
            {this.state.errorInfo?.componentStack || this.state.error?.stack}
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: '#f59e0b',
              color: '#0f172a',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Reload Application
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Global error handlers to capture startup crashes
window.addEventListener('error', (e) => {
  console.error('Global JS Error:', e.error || e.message);
});

window.addEventListener('unhandledrejection', (e) => {
  console.error('Unhandled Promise Rejection:', e.reason);
});

// Environment detection & status bar styling
const setupEnvironmentAndTheme = () => {
  if (typeof window === 'undefined') return;

  const isCapacitor = typeof window.Capacitor !== 'undefined' && window.Capacitor.isNativePlatform?.();
  const isStandalonePwa = window.matchMedia('(display-mode: standalone)').matches || 
                          window.matchMedia('(display-mode: fullscreen)').matches ||
                          window.navigator.standalone === true;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                   (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 768px)').matches);

  const docEl = document.documentElement;
  const bodyEl = document.body;

  if (isCapacitor) {
    docEl.classList.add('is-capacitor-app');
    bodyEl.classList.add('is-capacitor-app');
  } else {
    if (isMobile) {
      docEl.classList.add('is-mobile-web');
      bodyEl.classList.add('is-mobile-web');
    }
    if (isStandalonePwa) {
      docEl.classList.add('is-pwa-standalone');
      bodyEl.classList.add('is-pwa-standalone');
    } else {
      docEl.classList.add('is-browser');
      bodyEl.classList.add('is-browser');
    }
  }

  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', '#0D152D');
  }
};
setupEnvironmentAndTheme();
installAvatarFallback();

const rootElement = document.getElementById('root');
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>
  );
}

reportWebVitals();
